import type { ChangeEvent, UIEvent } from 'react';
import React, { useState } from 'react';

import {
  getGroup,
  useGetGroups,
  useGetGroupsInfinite,
  usePutGroup,
} from '@vlabs/api/artemy/client';
import type {
  GroupRecord,
  GroupId,
  GroupsList,
  PutGroupRequest,
} from '@vlabs/api/artemy/model';
import { CheckIcon } from '@vlabs/icons';
import { DropdownMenu } from '@vlabs/uikit';
import { toast } from 'react-toastify';

import { confirmAttachDepartmentGroups } from '@vlabs/monokit/entities/confirm-attach-department-groups';

import { useAttachDepartmentGroupsTranslation } from './i18n';
import type { AttachDepartmentGroupsProps } from './types';

const POPUP_WIDTH = 280;
const POPUP_MAX_HEIGHT = 360;
const DEFAULT_GROUPS_PAGE_SIZE = 10;

interface AttachDepartmentGroupsDropdownProps extends AttachDepartmentGroupsProps {
  children: React.ReactElement;
  disabled?: boolean;
}

function getGroupsFromPages(pages?: GroupsList[]): GroupRecord[] {
  return pages?.flatMap(({ groups }) => groups ?? []) ?? [];
}

function getNextPageParam(lastPage: GroupsList, pages: GroupsList[]): number | undefined {
  return lastPage.groups?.length === DEFAULT_GROUPS_PAGE_SIZE ? pages.length + 1 : undefined;
}

function toPutGroupData(group: GroupRecord, ownerDepartment: GroupRecord['owner_department']): PutGroupRequest {
  const data: PutGroupRequest = {
    description: group.description,
    group_name: group.group_name,
    owner_department: ownerDepartment,
    permission_tag: group.permission_tag,
  };

  // бэк временно не принимает пустые optional-поля в PUT группы, поэтому не отправляем их
  if (group.analytics?.length) {
    data.analytics = group.analytics;
  }

  if (Object.keys(group.stream_settings ?? {}).length) {
    data.stream_settings = group.stream_settings;
  }

  return data;
}

function AttachDepartmentGroupsDropdown({
  children,
  departmentId,
  disabled = false,
}: AttachDepartmentGroupsDropdownProps): React.JSX.Element {
  const { t } = useAttachDepartmentGroupsTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');

  const getGroups = useGetGroupsInfinite(undefined, {
    query: {
      enabled: isOpen,
      getNextPageParam,
      initialPageParam: 1,
    },
  });
  const groups = getGroupsFromPages(getGroups.data?.pages);
  const normalizedQuery = query.trim().toLowerCase();
  const filteredGroups = normalizedQuery
    ? groups.filter(({ group_name: groupName }) => groupName?.toLowerCase().includes(normalizedQuery))
    : groups;
  const groupIds = groups.map(({ group_id: groupId }) => groupId);
  const getGroupsDetails = useGetGroups(
    {
      group_ids: groupIds.join(','),
      page_size: groupIds.length,
    },
    { query: { enabled: isOpen && groupIds.length > 0 } },
  );

  const putGroup = usePutGroup({
    mutation: {
      onSuccess: () => {
        toast.success(t('on_success'));
        setIsOpen(false);
      },
    },
  });

  const handleOpenChange = (open: boolean): void => {
    setIsOpen(open);

    if (!open) {
      setQuery('');
    }
  };

  const handleAttachGroup = async (groupId: GroupId, groupDetails?: GroupRecord): Promise<void> => {
    if (groupDetails?.owner_department && groupDetails.owner_department !== departmentId) {
      const result = await confirmAttachDepartmentGroups({
        departmentId,
        groupIds: [groupId],
        onConfirm: () => undefined,
      });

      if (result === 'cancelled') return;
    }

    const groupForUpdate = await getGroup(groupId);

    await putGroup.mutateAsync({
      groupId,
      data: toPutGroupData(groupForUpdate, departmentId),
    });
  };

  const handleQueryChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setQuery(e.target.value);
  };

  const groupsDetails = getGroupsDetails.data?.groups ?? [];
  const isLoading = isOpen && (getGroups.isLoading || getGroupsDetails.isLoading);
  const hasError = getGroups.isError || getGroupsDetails.isError;
  const canShowGroups = !isLoading && !hasError;
  const isDisabled = disabled || putGroup.isPending;

  const handlePopupScroll = (e: UIEvent<HTMLDivElement>): void => {
    const { clientHeight, scrollHeight, scrollTop } = e.currentTarget;
    const isScrolledToBottom = scrollHeight - scrollTop - clientHeight < 8;

    if (canShowGroups && getGroups.hasNextPage && !getGroups.isFetchingNextPage && isScrolledToBottom) {
      getGroups.fetchNextPage();
    }
  };

  let item: React.ReactNode = null;

  if (isLoading) {
    item = <DropdownMenu.EmptyItem>{t('loading')}</DropdownMenu.EmptyItem>;
  } else if (hasError) {
    item = <DropdownMenu.EmptyItem>{t('load_error')}</DropdownMenu.EmptyItem>;
  } else if (canShowGroups && filteredGroups.length === 0) {
    item = <DropdownMenu.EmptyItem>{t('empty')}</DropdownMenu.EmptyItem>;
  } else if (canShowGroups) {
    item = filteredGroups.map((group) => {
      const groupDetails = groupsDetails.find(({ group_id: groupId }) => groupId === group.group_id);
      const isAttached = groupDetails?.owner_department === departmentId;

      return (
        <DropdownMenu.Item
          decorationRight={isAttached ? <CheckIcon /> : undefined}
          disabled={putGroup.isPending || isAttached}
          key={group.group_id}
          onClick={() => {
            handleAttachGroup(group.group_id, groupDetails).catch(() => {
              toast.error(t('on_error'));
            });
          }}
        >
          {group.group_name}
        </DropdownMenu.Item>
      );
    });
  }

  return (
    <DropdownMenu.Root
      modal={false}
      onOpenChange={handleOpenChange}
      open={isOpen}
    >
      <DropdownMenu.Trigger
        disabled={isDisabled}
        render={children}
      />
      <DropdownMenu.Portal>
        <DropdownMenu.Positioner>
          <DropdownMenu.Popup
            onScroll={handlePopupScroll}
            style={{
              maxHeight: POPUP_MAX_HEIGHT,
              overflowY: 'auto',
              width: POPUP_WIDTH,
            }}
          >
            <DropdownMenu.SearchItem
              onChange={handleQueryChange}
              placeholder={t('search_placeholder') as unknown as string}
              size="small"
              value={query}
            />
            {item}
          </DropdownMenu.Popup>
        </DropdownMenu.Positioner>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

export { AttachDepartmentGroupsDropdown };
